import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  Handshake,
  ReceiptText,
  UsersRound,
  ShieldCheck,
  LogOut,
  ChevronRight,
  KeyRound,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const roleLabel = {
  admin: "Admin",
  client_team: "Client Team",
  partner_team: "Partner Team",
};

function SidebarBody({ links, user, roleLabel, onOpenPw, onLogout, onCloseMobile }) {
  return (
    <>
      <div className="px-5 py-5 border-b border-line flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <img src="/brand/aisensy-dark.svg" alt="AiSensy" className="h-6 w-auto" />
        </Link>
        {onCloseMobile && (
          <button
            type="button"
            onClick={onCloseMobile}
            data-testid="sidebar-close"
            aria-label="Close menu"
            className="p-1 text-ink-2 hover:text-ink"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        )}
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={l.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                isActive
                  ? "bg-[color:var(--bone)] text-[color:var(--aisensy-dark)] font-medium border-l-2 border-[color:var(--aisensy-green)]"
                  : "text-ink-2 hover:text-ink hover:bg-[color:var(--bone)] border-l-2 border-transparent"
              }`
            }
          >
            <l.icon className="w-4 h-4" strokeWidth={1.75} />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-line p-4">
        <div className="eyebrow mb-1">Signed in as</div>
        <div className="text-sm font-medium text-ink truncate" data-testid="sidebar-user-name">
          {user?.name}
        </div>
        <div className="text-xs text-ink-2 truncate">{user?.email}</div>
        <div
          className="mt-2 inline-block text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 bg-[color:var(--aisensy-green)]/10 text-[color:var(--aisensy-dark)] rounded-sm"
          data-testid="sidebar-user-role"
        >
          {roleLabel[user?.role] || user?.role}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenPw}
          data-testid="change-password-btn"
          className="mt-3 w-full justify-start px-2 rounded-sm text-ink-2 hover:text-ink"
        >
          <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.75} /> Change password
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogout}
          data-testid="logout-btn"
          className="mt-1 w-full justify-start px-2 rounded-sm text-ink-2 hover:text-ink"
        >
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} /> Sign out
        </Button>
      </div>
    </>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitPw = async () => {
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    if (nw !== nw2) return toast.error("Passwords do not match");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setPwOpen(false);
      setCur(""); setNw(""); setNw2("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwBusy(false);
    }
  };

  const showClients = user?.role === "admin" || user?.role === "client_team";
  const showPartners = user?.role === "admin" || user?.role === "partner_team";

  const links = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    showClients && { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    showPartners && { to: "/partners", label: "Partners", icon: Handshake, testid: "nav-partners" },
    { to: "/invoices", label: "Invoices", icon: ReceiptText, testid: "nav-invoices" },
    user?.role === "admin" && { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team" },
    user?.role === "admin" && { to: "/audit", label: "Audit Log", icon: ShieldCheck, testid: "nav-audit" },
  ].filter(Boolean);

  const currentTitle = links.find((l) => l.to === location.pathname)?.label || "Dashboard";

  return (
    <div className="min-h-screen flex bg-bone">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
          className="md:hidden fixed inset-y-0 right-0 left-60 z-30 bg-black/40"
        />
      )}

      {/* Desktop sidebar (static, sticky) */}
      <aside
        data-testid="sidebar-desktop"
        className="hidden md:flex sticky top-0 h-screen w-60 shrink-0 border-r border-line bg-card flex-col"
      >
        <SidebarBody
          links={links}
          user={user}
          roleLabel={roleLabel}
          onOpenPw={() => setPwOpen(true)}
          onLogout={logout}
          onCloseMobile={null}
        />
      </aside>

      {/* Mobile sidebar drawer (only mounted when open) */}
      {mobileOpen && (
        <aside
          data-testid="sidebar"
          className="md:hidden fixed z-40 top-0 left-0 h-screen w-60 border-r border-line bg-card flex flex-col"
        >
          <SidebarBody
            links={links}
            user={user}
            roleLabel={roleLabel}
            onOpenPw={() => setPwOpen(true)}
            onLogout={logout}
            onCloseMobile={() => setMobileOpen(false)}
          />
        </aside>
      )}

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Change password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="eyebrow">Current password</Label>
              <Input
                type="password"
                className="mt-1"
                value={cur}
                onChange={(e) => setCur(e.target.value)}
                data-testid="cp-current"
                required
              />
            </div>
            <div>
              <Label className="eyebrow">New password</Label>
              <Input
                type="password"
                className="mt-1"
                value={nw}
                onChange={(e) => setNw(e.target.value)}
                data-testid="cp-new"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label className="eyebrow">Confirm new password</Label>
              <Input
                type="password"
                className="mt-1"
                value={nw2}
                onChange={(e) => setNw2(e.target.value)}
                data-testid="cp-new2"
                required
                minLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={submitPw}
              disabled={pwBusy}
              className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
              data-testid="cp-submit"
            >
              {pwBusy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-line bg-card flex items-center px-4 md:px-6 justify-between">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              data-testid="sidebar-open"
              aria-label="Open menu"
              className="md:hidden p-1.5 -ml-1.5 mr-1 text-ink-2 hover:text-ink"
            >
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            </button>
            <span className="text-ink-2 hidden sm:inline">AiSensy · Receivables</span>
            <ChevronRight className="w-3.5 h-3.5 text-ink-2 hidden sm:inline" />
            <span className="font-medium truncate" data-testid="page-title">
              {currentTitle}
            </span>
          </div>
          <div className="text-xs text-ink-2 tabular whitespace-nowrap">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 md:p-6" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
